import { TFunction } from 'react-i18next'

import { Chip, Typography } from '@mui/material'

import { GRTableColumn, SortOrder } from '../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../components/GRTooltip/GRTooltip'
import { CreativeTag, ObjectId, TermRef } from '../types'
import { clickableProps } from './interactive'
import { clickableChipSx, linkTypographyBoldSx, linkTypographySx } from './styles'
import { UATaxonomyLabels } from './taxonomyLabels'

/**
 * Column: tag dimension / group (e.g. "setting" → "Setting"). When
 * `onDimensionClick` is provided the cell renders as a primary-coloured link
 * that opens the group-level drill-down; otherwise it falls back to plain
 * text.
 */
export const dimensionColumn = (
  t: TFunction,
  taxonomy: UATaxonomyLabels,
  onDimensionClick?: (dimensionId: ObjectId) => void,
  opts?: { width?: number }
): GRTableColumn<CreativeTag, undefined> => ({
  labelAccessor: t('col_group'),
  accessor: onDimensionClick
    ? ({ row }) => (
        <GRTooltip content={taxonomy.dimensionLabel(row.tag.dimensionId)} styles={{ display: 'block' }} tooltipProps={{ enterDelay: 700, enterNextDelay: 300 }}>
          <Typography variant="body2" sx={linkTypographySx} {...clickableProps(() => onDimensionClick(row.tag.dimensionId))}>
            {taxonomy.dimensionLabel(row.tag.dimensionId)}
          </Typography>
        </GRTooltip>
      )
    : ({ row }) => taxonomy.dimensionLabel(row.tag.dimensionId),
  sortAccessor: ({ row }) => taxonomy.dimensionLabel(row.tag.dimensionId),
  sortable: true,
  width: opts?.width,
  cellProps: { align: 'left' },
  headerCellProps: { align: 'left', sx: { fontWeight: 'bold' } },
})

/**
 * Column: clickable tag name rendered as a primary-coloured bold link.
 * Used by every table that lists `CreativeTag` rows.
 */
export const tagNameColumn = (
  t: TFunction,
  taxonomy: UATaxonomyLabels,
  onTagClick: (tagId: ObjectId) => void,
  opts?: { cellProps?: GRTableColumn<CreativeTag, undefined>['cellProps']; width?: number }
): GRTableColumn<CreativeTag, undefined> => ({
  labelAccessor: t('col_tag_name'),
  accessor: ({ row }) => (
    <GRTooltip content={taxonomy.tagLabel(row.tag)} styles={{ display: 'block' }} tooltipProps={{ enterDelay: 700, enterNextDelay: 300 }}>
      <Typography variant="body2" sx={linkTypographyBoldSx} {...clickableProps(() => onTagClick(row.tag.id))}>
        {taxonomy.tagLabel(row.tag)}
      </Typography>
    </GRTooltip>
  ),
  sortAccessor: ({ row }) => taxonomy.tagLabel(row.tag),
  sortable: true,
  width: opts?.width,
  cellProps: opts?.cellProps ?? { align: 'left' },
  headerCellProps: { align: 'left', sx: { fontWeight: 'bold' } },
})

/**
 * Column: "most common genre". Renders as a clickable link when `onGenreClick`
 * is provided, otherwise as plain text.
 */
export const isUnknownGenre = (genre: TermRef | undefined): genre is undefined => !genre || genre.code.trim().toLowerCase() === 'unknown'

export const genreColumn = (
  t: TFunction,
  taxonomy: UATaxonomyLabels,
  onGenreClick?: (genreId: ObjectId) => void,
  opts?: { width?: number }
): GRTableColumn<CreativeTag, undefined> => ({
  labelAccessor: t('col_genre'),
  accessor: ({ row }) => {
    const genre = row.mostCommonGenre
    if (isUnknownGenre(genre)) {
      return (
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {t('common:not_available_shorthand')}
        </Typography>
      )
    }
    const label = taxonomy.genreLabel(genre)
    if (onGenreClick) {
      return (
        <GRTooltip content={label} styles={{ display: 'block' }} tooltipProps={{ enterDelay: 700, enterNextDelay: 300 }}>
          <Typography variant="body2" sx={linkTypographySx} {...clickableProps(() => onGenreClick(genre.id))}>
            {label}
          </Typography>
        </GRTooltip>
      )
    }
    return label
  },
  sortAccessor: ({ row }) => (isUnknownGenre(row.mostCommonGenre) ? '' : taxonomy.genreLabel(row.mostCommonGenre)),
  sortable: true,
  width: opts?.width,
  cellProps: { align: 'left' },
  headerCellProps: { align: 'left', sx: { fontWeight: 'bold' } },
})

/**
 * Column: number of games carrying this tag. Optionally clickable; when a click
 * handler is provided the number renders as a primary-coloured link.
 */
export const gamesCountColumn = (
  t: TFunction,
  opts?: { align?: 'left' | 'center'; width?: number; onClick?: (tagId: ObjectId) => void; sortOrder?: SortOrder }
): GRTableColumn<CreativeTag, undefined> => {
  const align = opts?.align ?? 'center'
  const onClick = opts?.onClick
  return {
    labelAccessor: t('col_games'),
    accessor: onClick
      ? ({ row }) => (
          <Typography variant="body2" sx={linkTypographySx} {...clickableProps(() => onClick(row.tag.id))}>
            {row.gamesCount}
          </Typography>
        )
      : ({ row }) => row.gamesCount,
    sortAccessor: 'gamesCount',
    sortable: true,
    sortOrder: opts?.sortOrder,
    width: opts?.width ?? 80,
    cellProps: { align },
    headerCellProps: { align, sx: { fontWeight: 'bold' } },
  }
}

/**
 * Column: number of creatives in which this tag appears. Optionally clickable;
 * when a click handler is provided the number renders as a primary-coloured
 * link.
 */
export const creativesCountColumn = (
  t: TFunction,
  opts?: { align?: 'left' | 'center'; width?: number; onClick?: (tagId: ObjectId) => void; sortOrder?: SortOrder }
): GRTableColumn<CreativeTag, undefined> => {
  const align = opts?.align ?? 'center'
  const onClick = opts?.onClick
  return {
    labelAccessor: t('col_creatives'),
    accessor: onClick
      ? ({ row }) => (
          <Typography variant="body2" sx={linkTypographySx} {...clickableProps(() => onClick(row.tag.id))}>
            {row.creativesCount ?? 0}
          </Typography>
        )
      : ({ row }) => row.creativesCount ?? 0,
    sortAccessor: ({ row }) => row.creativesCount ?? 0,
    sortable: true,
    sortOrder: opts?.sortOrder,
    width: opts?.width ?? 100,
    cellProps: { align },
    headerCellProps: { align, sx: { fontWeight: 'bold' } },
  }
}

/**
 * Column: average motivation shown as a (optionally clickable) chip.
 */
export const motivationChipColumn = (
  t: TFunction,
  taxonomy: UATaxonomyLabels,
  onMotivationClick?: (motivationId: ObjectId) => void
): GRTableColumn<CreativeTag, undefined> => ({
  labelAccessor: t('col_motivation'),
  accessor: ({ row }) => {
    const motivation = row.avgMotivation
    if (!motivation) {
      return (
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {t('common:not_available_shorthand')}
        </Typography>
      )
    }
    return (
      <Chip
        label={taxonomy.motivationLabel(motivation)}
        size="small"
        onClick={onMotivationClick ? () => onMotivationClick(motivation.id) : undefined}
        sx={onMotivationClick ? clickableChipSx : undefined}
      />
    )
  },
  sortAccessor: ({ row }) => (row.avgMotivation ? taxonomy.motivationLabel(row.avgMotivation) : ''),
  sortable: true,
  cellProps: { align: 'left' },
  headerCellProps: { align: 'left', sx: { fontWeight: 'bold' } },
})

export interface CreativeTagListingHandlers {
  onTagClick: (tagId: ObjectId) => void
  onTagGamesClick: (tagId: ObjectId) => void
  onGenreClick: (genreId: ObjectId) => void
  onMotivationClick: (motivationId: ObjectId) => void
}

/**
 * The "most prevalent creative tags" column set, shared by the dimension and
 * genre drill-downs so the two cannot drift apart. Rows rank by games count
 * descending. The all-tags listing deliberately uses a different set — it
 * leads with the dimension column and ranks by creatives count instead.
 */
export const creativeTagListingColumns = (
  t: TFunction,
  taxonomy: UATaxonomyLabels,
  handlers: CreativeTagListingHandlers
): GRTableColumn<CreativeTag, undefined>[] => [
  tagNameColumn(t, taxonomy, handlers.onTagClick),
  creativesCountColumn(t, { align: 'center', onClick: handlers.onTagClick }),
  gamesCountColumn(t, { align: 'center', onClick: handlers.onTagGamesClick, sortOrder: SortOrder.DESC }),
  genreColumn(t, taxonomy, handlers.onGenreClick),
  motivationChipColumn(t, taxonomy, handlers.onMotivationClick),
]
